import { useCallback, useEffect, useRef, useState } from 'react';

import { useWavesurfer } from '@wavesurfer/react';
import type { WaveSurferOptions } from 'wavesurfer.js';

import { callGet } from '@/api/fetcher';

export interface UseWaveformPlayerOptions {
  /** URL to fetch audio from (can be null/undefined to defer loading) */
  url: string | null | undefined;
  /** Use authenticated fetch via callGet (for internal API endpoints) */
  useAuth?: boolean;
  /** Initial duration hint for display before audio loads */
  initialDuration?: number;
  /** Waveform color for unplayed portion */
  waveColor?: string;
  /** Waveform color for played portion */
  progressColor?: string;
  /** Width of each bar in pixels */
  barWidth?: number;
  /** Gap between bars in pixels */
  barGap?: number;
  /** Height of the waveform in pixels */
  height?: number;
}

export interface UseWaveformPlayerReturn {
  /** Ref to attach to the container element */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Whether wavesurfer is ready to play */
  isReady: boolean;
  /** Whether audio is currently playing */
  isPlaying: boolean;
  /** Current playback position in seconds */
  currentTime: number;
  /** Total duration in seconds */
  duration: number;
  /** Whether audio is being fetched */
  isLoading: boolean;
  /** The fetched audio blob (for download functionality) */
  blob: Blob | null;
  /** Current playback rate */
  playbackRate: number;
  /** Toggle play/pause */
  playPause: () => void;
  /** Seek to a position (0-1 ratio) */
  seekTo: (ratio: number) => void;
  /** Set playback speed (e.g. 0.5, 1, 1.5, 2) */
  setPlaybackRate: (rate: number) => void;
}

/**
 * A reusable hook for audio playback with interactive waveform visualization.
 *
 * Uses wavesurfer.js under the hood to provide:
 * - Waveform rendering
 * - Click-to-seek functionality
 * - Drag-to-scrub functionality
 * - Play/pause controls
 *
 * For authenticated endpoints, set `useAuth: true`
 * to use the app's fetcher with proper credentials.
 */
export const useWaveformPlayer = ({
  url,
  useAuth = false,
  initialDuration = 0,
  waveColor = '#94A3B8',
  progressColor = '#1d4ed8',
  barWidth = 2,
  barGap = 3,
  height = 24,
}: UseWaveformPlayerOptions): UseWaveformPlayerReturn => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [playbackRate, setPlaybackRateState] = useState(1);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration);

  // Wavesurfer configuration
  const wavesurferOptions: Omit<WaveSurferOptions, 'container'> & {
    container: React.RefObject<HTMLDivElement | null>;
  } = {
    container: containerRef,
    url: blobUrl ?? undefined,
    waveColor,
    progressColor,
    barWidth,
    barGap,
    height,
    cursorWidth: 0, // Hide cursor line
    normalize: true, // Normalize waveform peaks
  };

  const { wavesurfer, isReady, isPlaying } = useWavesurfer(wavesurferOptions);

  // Fetch audio blob when URL changes
  useEffect(() => {
    if (!url) {
      setBlob(null);
      setBlobUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return null;
      });
      setIsLoading(false);
      setCurrentTime(0);
      setDuration(initialDuration);
      return;
    }

    const abortController = new AbortController();
    setIsLoading(true);
    setCurrentTime(0);
    setDuration(initialDuration);

    // Revoke previous blob URL
    setBlobUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return null;
    });
    setBlob(null);

    const fetchAudio = async () => {
      try {
        let audioBlob: Blob;

        if (useAuth) {
          // Use authenticated fetcher for internal API endpoints
          audioBlob = await callGet(url, { responseType: 'blob', signal: abortController.signal });
        } else {
          // Use regular fetch for external URLs
          const response = await fetch(url, { signal: abortController.signal });
          if (!response.ok) {
            throw new Error(`Failed to fetch audio: ${response.status}`);
          }
          audioBlob = await response.blob();
        }

        // Create blob URL for wavesurfer
        const newBlobUrl = URL.createObjectURL(audioBlob);
        setBlob(audioBlob);
        setBlobUrl(newBlobUrl);
        setIsLoading(false);
      } catch (error) {
        // Ignore aborted requests
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        if (abortController.signal.aborted) {
          return;
        }
        console.error('Failed to load audio:', error);
        setBlob(null);
        setBlobUrl(null);
        setIsLoading(false);
      }
    };

    fetchAudio();

    return () => {
      abortController.abort();
    };
  }, [url, useAuth, initialDuration]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  // Subscribe to wavesurfer events
  useEffect(() => {
    if (!wavesurfer) {
      return;
    }

    // Sync playback rate when wavesurfer is (re)created
    if (playbackRate !== 1) {
      wavesurfer.setPlaybackRate(playbackRate);
    }

    const handleTimeUpdate = (time: number) => {
      setCurrentTime(time);
    };

    const handleDecode = (dur: number) => {
      setDuration(dur);
    };

    const handleFinish = () => {
      setCurrentTime(0);
      wavesurfer.seekTo(0);
    };

    wavesurfer.on('timeupdate', handleTimeUpdate);
    wavesurfer.on('decode', handleDecode);
    wavesurfer.on('finish', handleFinish);

    return () => {
      wavesurfer.un('timeupdate', handleTimeUpdate);
      wavesurfer.un('decode', handleDecode);
      wavesurfer.un('finish', handleFinish);
    };
  }, [wavesurfer]);

  const playPause = useCallback(() => {
    wavesurfer?.playPause();
  }, [wavesurfer]);

  const seekTo = useCallback(
    (ratio: number) => {
      wavesurfer?.seekTo(ratio);
    },
    [wavesurfer]
  );

  const setPlaybackRate = useCallback(
    (rate: number) => {
      setPlaybackRateState(rate);
      wavesurfer?.setPlaybackRate(rate);
    },
    [wavesurfer]
  );

  return {
    containerRef,
    isReady,
    isPlaying: isPlaying ?? false,
    currentTime,
    duration,
    isLoading: isLoading || (!isReady && !!blobUrl),
    playbackRate,
    blob,
    playPause,
    seekTo,
    setPlaybackRate,
  };
};
